import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Cabecalho, Layout } from '../componentes/Layout.tsx';
import { Area, Aviso, Botao, Campo } from '../componentes/forma.tsx';
import { api, ErroApi } from '../lib/api.ts';
import { formatarTelefone, soDigitos } from '../lib/formato.ts';
import './configuracoes.css';

interface Form {
  temLogo?: boolean;
  nome: string;
  endereco: string;
  telefone: string;
  site: string;
  msgPaciente: string;
  msgMedico: string;
}

const VAZIO: Form = { nome: '', endereco: '', telefone: '', site: '', msgPaciente: '', msgMedico: '' };

// {{pdf}} abre o documento direto, sem a data de nascimento, e vale por poucos
// dias. {{link}} é o caminho com a confirmação, que continua valendo sempre --
// vale a pena manter os dois na mensagem.
const CAMPOS_PACIENTE = ['{{paciente}}', '{{codigo}}', '{{procedimento}}', '{{data}}',
  '{{pdf}}', '{{dias}}', '{{link}}', '{{clinica}}'];
const CAMPOS_MEDICO = ['{{medico}}', '{{quantidade}}', '{{mais_antigo}}', '{{link}}', '{{clinica}}'];

export function Configuracoes() {
  const qc = useQueryClient();
  const [form, setForm] = useState<Form>(VAZIO);
  const [campos, setCampos] = useState<Record<string, string>>({});
  const [erro, setErro] = useState('');
  const [salvo, setSalvo] = useState('');
  const arquivo = useRef<HTMLInputElement>(null);
  const [versaoLogo, setVersaoLogo] = useState(0);

  const dados = useQuery({
    queryKey: ['clinica'],
    queryFn: ({ signal }) => api.get<Partial<Form>>('/aviso/clinica', signal),
  });

  useEffect(() => {
    if (dados.data) setForm({ ...VAZIO, ...(dados.data as Form) });
  }, [dados.data]);

  const salvar = useMutation({
    mutationFn: () => api.put('/aviso/clinica', form),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clinica'] });
      void qc.invalidateQueries({ queryKey: ['aviso'] });
      setSalvo('Configurações salvas.');
      setErro('');
      setCampos({});
    },
    onError: (e) => {
      setSalvo('');
      if (e instanceof ErroApi) {
        setCampos(e.campos);
        if (!e.temCampos) setErro(e.message);
      } else setErro('Não foi possível salvar.');
    },
  });

  async function enviarLogo(arq: File) {
    setErro('');
    setSalvo('');
    const dataUrl = await new Promise<string>((resolver, rejeitar) => {
      const leitor = new FileReader();
      leitor.onload = () => resolver(String(leitor.result));
      leitor.onerror = () => rejeitar(new Error('falha ao ler o arquivo'));
      leitor.readAsDataURL(arq);
    });
    try {
      await api.put('/aviso/clinica/logo', { logo: dataUrl });
      setForm((f) => ({ ...f, temLogo: true }));
      setVersaoLogo((v) => v + 1);
      // A marca aparece em todas as telas; sem isto o menu continuaria com o
      // nome em texto até alguém recarregar a página.
      void qc.invalidateQueries({ queryKey: ['clinica-publica'] });
      setSalvo('Logo atualizada.');
    } catch (e) {
      setErro(e instanceof ErroApi ? (e.campos.logo ?? e.message) : 'Não foi possível enviar.');
    }
  }

  async function removerLogo() {
    if (!confirm('Remover a logo? O nome da clínica volta a aparecer em texto.')) return;
    await api.apagar('/aviso/clinica/logo');
    setForm((f) => ({ ...f, temLogo: false }));
    setVersaoLogo((v) => v + 1);
    void qc.invalidateQueries({ queryKey: ['clinica-publica'] });
    setSalvo('Logo removida.');
  }

  const mudar =
    (chave: keyof Form, mascara?: (v: string) => string) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [chave]: mascara ? mascara(e.target.value) : e.target.value }));

  return (
    <Layout>
      <Cabecalho titulo="Configurações da clínica" />

      <form
        className="config"
        onSubmit={(e) => {
          e.preventDefault();
          salvar.mutate();
        }}
        noValidate
      >
        <section className="cartao config__bloco">
          <header className="config__topo">
            <h2 className="config__titulo">Identificação</h2>
            <p className="campo__dica">
              Aparece no cabeçalho e no rodapé de todo laudo em PDF, e na tela pública de
              retirada de exame.
            </p>
          </header>
          <div className="grade config__campos">
            {erro && (
              <div className="campo campo--total">
                <Aviso>{erro}</Aviso>
              </div>
            )}
            {salvo && (
              <div className="campo campo--total">
                <Aviso tipo="sucesso">{salvo}</Aviso>
              </div>
            )}

            <Campo rotulo="Nome" obrigatorio value={form.nome} erro={campos.nome} onChange={mudar('nome')} />
            <Campo rotulo="Endereço" value={form.endereco} erro={campos.endereco} onChange={mudar('endereco')} />
            <Campo
              rotulo="Telefone"
              largura="meia"
              inputMode="numeric"
              value={formatarTelefone(form.telefone)}
              erro={campos.telefone}
              onChange={mudar('telefone', soDigitos)}
            />
            <Campo
              rotulo="Endereço do sistema na internet"
              largura="meia"
              placeholder="https://exames.centromedico.com.br"
              value={form.site}
              erro={campos.site}
              onChange={mudar('site')}
              dica="É a base do link enviado ao paciente. Sem ele, o aviso sai quebrado."
            />
          </div>
        </section>

        <section className="cartao config__bloco">
          <header className="config__topo">
            <h2 className="config__titulo">Logo</h2>
            <p className="campo__dica">
              Aparece no menu, na entrada, na tela pública e no cabeçalho de todo laudo em PDF.
              PNG ou JPEG, até 2 MB — de preferência com fundo transparente.
            </p>
          </header>
          <div className="config__campos">
            <div className="config__logo">
              {form.temLogo ? (
                <img src={`/api/publico/logo?v=${versaoLogo}`} alt="Logo da clínica" />
              ) : (
                <p className="campo__dica">Nenhuma logo enviada — o nome aparece em texto.</p>
              )}
            </div>
            <input
              ref={arquivo}
              type="file"
              accept="image/png,image/jpeg"
              className="so-leitor"
              onChange={(e) => {
                const arq = e.target.files?.[0];
                if (arq) void enviarLogo(arq);
                e.target.value = '';
              }}
            />
            <div className="config__logo-acoes">
              <Botao type="button" variante="neutro" tamanho="sm" onClick={() => arquivo.current?.click()}>
                {form.temLogo ? 'Trocar logo' : 'Enviar logo'}
              </Botao>
              {form.temLogo && (
                <Botao type="button" variante="perigo" tamanho="sm" onClick={() => void removerLogo()}>
                  Remover
                </Botao>
              )}
            </div>
          </div>
        </section>

        <section className="cartao config__bloco">
          <header className="config__topo">
            <h2 className="config__titulo">Mensagem para o paciente</h2>
            <p className="campo__dica">
              Enviada pelo WhatsApp quando o laudo fica pronto. O link leva à tela pública, que
              ainda pede a data de nascimento — por isso o PDF nunca vai anexado.
            </p>
          </header>
          <div className="grade config__campos">
            <Area
              rotulo="Texto"
              rows={8}
              value={form.msgPaciente}
              erro={campos.msgPaciente}
              onChange={mudar('msgPaciente')}
            />
            <Etiquetas campos={CAMPOS_PACIENTE} />
          </div>
        </section>

        <section className="cartao config__bloco">
          <header className="config__topo">
            <h2 className="config__titulo">Mensagem para o médico</h2>
            <p className="campo__dica">
              Um resumo por médico, não uma mensagem por exame. Não inclua nome de paciente: o
              aviso passa por um aplicativo pessoal.
            </p>
          </header>
          <div className="grade config__campos">
            <Area
              rotulo="Texto"
              rows={7}
              value={form.msgMedico}
              erro={campos.msgMedico}
              onChange={mudar('msgMedico')}
            />
            <Etiquetas campos={CAMPOS_MEDICO} />
          </div>
        </section>

        <div className="config__acoes">
          <Botao type="submit" variante="primario" carregando={salvar.isPending}>
            {salvar.isPending ? 'Salvando…' : 'Salvar configurações'}
          </Botao>
        </div>
      </form>
    </Layout>
  );
}

function Etiquetas({ campos }: { campos: string[] }) {
  return (
    <div className="campo campo--total">
      <p className="rotulo">Campos que o sistema substitui</p>
      <div className="config__etiquetas">
        {campos.map((c) => (
          <code className="config__etiqueta" key={c}>
            {c}
          </code>
        ))}
      </div>
    </div>
  );
}
